import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get_north_star } from '../utils/services';

// Caché a nivel de módulo: al re-montar (p.ej. al colapsar el sidebar) mostramos el dato
// cacheado al instante en vez de parpadear a vacío mientras el fetch vuelve.
let northStarCache = null;

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MONTHS_ES[parseInt(m, 10) - 1] || m} ${y}`;
}

// Delta en % si el mes previo > 0 (p.ej. +66%); si viene de 0, absoluto (+3).
function deltaText(cur, prev) {
  const d = cur - prev;
  if (prev > 0) {
    const pct = Math.round((d / prev) * 100);
    return `${pct >= 0 ? '+' : ''}${pct}%`;
  }
  return `${d >= 0 ? '+' : ''}${d}`;
}
const arrow = (d) => (d > 0 ? '▲' : d < 0 ? '▼' : '—');
const deltaColor = (d) => (d > 0 ? '#16a34a' : d < 0 ? '#dc2626' : '#9ca3af'); // sobre blanco
const deltaColorDark = (d) => (d > 0 ? '#4ade80' : d < 0 ? '#f87171' : '#9ca3af'); // sobre la barra oscura

function Sparkline({ data, color, width = 116, height = 28 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const stepX = width / (data.length - 1 || 1);
  const xy = (v, i) => [i * stepX, height - ((v - min) / range) * (height - 5) - 3];
  const pts = data.map((v, i) => xy(v, i).map((n) => n.toFixed(1)).join(',')).join(' ');
  const [lx, ly] = xy(data[data.length - 1], data.length - 1);
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.5" fill={color} />
    </svg>
  );
}

// Una meta dentro del recuadro gris: etiqueta, número + delta, y sparkline debajo.
function MetricColumn({ label, metric, color }) {
  return (
    <div style={{ flex: 1, padding: '2px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 6, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{metric.current}</span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: deltaColor(metric.delta) }}>
          {arrow(metric.delta)} {deltaText(metric.current, metric.previous)}
        </span>
      </div>
      <div style={{ marginTop: 8 }}>
        <Sparkline data={metric.series} color={color} />
      </div>
    </div>
  );
}

export default function NorthStar({ token }) {
  const [data, setData] = useState(northStarCache);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    get_north_star(token).then((d) => {
      if (!cancelled && d) {
        northStarCache = d;
        setData(d);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  if (!data || !data.meta1 || !data.meta2) return null;
  const { meta1, meta2, month, projectId } = data;

  const goToGlobal = () => {
    setOpen(false);
    if (projectId != null) navigate(`/project?id=${projectId}`);
  };

  const pillStat = (tag, metric, title) => (
    <span title={title} style={{ whiteSpace: 'nowrap' }}>
      <span style={{ color: 'rgba(255,255,255,0.55)' }}>{tag} </span>
      <b style={{ color: '#fff' }}>{metric.current}</b>{' '}
      <span style={{ color: deltaColorDark(metric.delta), fontWeight: 600 }}>
        {arrow(metric.delta)}
        {deltaText(metric.current, metric.previous)}
      </span>
    </span>
  );

  return (
    <div className="app-header__northstar" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="North Star de empresa — clic para el detalle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 12px',
          borderRadius: 999,
          border: '1px solid rgba(255,255,255,0.12)',
          background: open ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)',
          color: '#fff',
          fontSize: 13,
          cursor: 'pointer',
          lineHeight: 1.2,
        }}
      >
        {pillStat('M1', meta1, 'Meta 1 · TOMis con uso ≥8h')}
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>·</span>
        {pillStat('M2', meta2, 'Meta 2 · Maestros con recuperación lograda')}
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
            border: '1px solid #e5e7eb',
            zIndex: 1000,
            padding: 12,
            cursor: 'default',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              background: '#f4f5f7',
              borderRadius: 12,
              padding: '12px 4px',
            }}
          >
            <MetricColumn label="Meta 1 · Uso ≥8h" metric={meta1} color="#5800ff" />
            <div style={{ width: 1, background: '#e2e4e9', margin: '2px 0' }} />
            <MetricColumn label="Meta 2 · Recuperación" metric={meta2} color="#16a34a" />
          </div>
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              fontSize: 11,
            }}
          >
            <span style={{ color: '#9ca3af' }}>{monthLabel(month)} · último mes cerrado</span>
            {projectId != null && (
              <button
                type="button"
                onClick={goToGlobal}
                title="Abrir el proyecto TOMi Global"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  color: '#9299a3',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#5800ff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#9299a3')}
              >
                TOMi Global ↗
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
